import React, { useEffect, useState } from "react";
import {
  getIsAuthenticatedSelector,
  checkAuthState,
  useAuthenticationDispatch,
  logout,
  useUserDataPreloader,
} from "@quest-finance/quest-fe-shared/dist/auth";
import Loading from "@quest-finance/quest-fe-shared/dist/common/components/Loading";
import NotFoundPage from "@quest-finance/quest-fe-shared/dist/common/components/NotFoundPage";
import ScrollToTop from "@quest-finance/quest-fe-shared/dist/common/components/ScrollToTop";
import APIService from "@quest-finance/quest-fe-shared/dist/common/services/APIService";
import { PageRoute } from "@quest-finance/quest-fe-shared/dist/common/types/PageRoute";
import { Notification } from "@quest-finance/quest-fe-shared/dist/notification";
import { AutoPageReload } from "@quest-finance/quest-fe-shared/dist/version-manager";
import { useSelector } from "react-redux";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router";
import { LayoutContext } from "../contexts/LayoutContext";
import { ForgotPasswordPage, IntroducerUserLogin } from "../pages/User";

const isProduction = process.env.NODE_ENV === "production";
const BaseLayout = React.lazy(() => import("./BaseLayout"));

const Utils = () => {
  const location = useLocation();

  return (
    <>
      <Notification maxVisible={2} />
      <ScrollToTop locationPathname={location.pathname} />
      {isProduction && <AutoPageReload location={location} />}
    </>
  );
};

type BaseRouteProps = {
  bannerRefreshShown?: boolean;
  routes: {
    publicRoutes: PageRoute[];
    privateRoutes: PageRoute[];
  };
};
const BaseRoute: React.FunctionComponent<BaseRouteProps> = ({
  bannerRefreshShown,
  routes,
}: BaseRouteProps) => {
  const isAuthenticated = useSelector(getIsAuthenticatedSelector);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const authDispatch = useAuthenticationDispatch();
  const preloader = useUserDataPreloader();

  useEffect(() => {
    authDispatch(checkAuthState());
    APIService.onTokenRefresh = (error) => {
      if (error) {
        window.location.href = "/auth/login";
        authDispatch(logout());
      }

      authDispatch(checkAuthState());
    };
  }, [authDispatch, isAuthenticated]);

  if (preloader.initialising) {
    return <Loading />;
  }

  return (
    <LayoutContext.Provider
      value={{
        isSidebarMinimized,
        setIsSidebarMinimized,
        showSidebar,
        setShowSidebar,
      }}
    >
      <BrowserRouter>
        <Utils />
        <React.Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/auth/login" element={<IntroducerUserLogin />} />
            <Route
              path="/auth/forgot-password"
              element={<ForgotPasswordPage />}
            />
            {routes.publicRoutes.map((route, index) => (
              <Route
                key={index}
                path={route.path}
                element={<route.component />}
              />
            ))}
            {routes.privateRoutes.map((route, index) => (
              <Route
                key={index}
                path={route.path}
                element={
                  <BaseLayout bannerRefreshShown={bannerRefreshShown}>
                    <route.component />
                  </BaseLayout>
                }
              />
            ))}
            <Route
              path="/"
              element={
                <Navigate
                  to={
                    !isAuthenticated
                      ? "/auth/login"
                      : "/application/applications"
                  }
                />
              }
            />
            <Route
              path="*"
              element={
                <NotFoundPage
                  redirectLink="/application/applications"
                  redirectLinkText="Back to dashboard"
                />
              }
            />
          </Routes>
        </React.Suspense>
      </BrowserRouter>
    </LayoutContext.Provider>
  );
};

export default BaseRoute;
